/**
 * Pricing page — Published PRO Work Unit pricing + Trial Access
 *
 * Public route (/pricing) — no auth required.
 * Shows the published Work Unit pricing ladder (2026-08 revamp — all numbers
 * import from src/config/workUnits.ts, never hard-coded here) and the
 * access-code trial redemption funnel.
 */

import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from '../components/ProfileImage';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import SeoHead from '../components/SeoHead';
import GlobalFooter from '../components/GlobalFooter';
import Modal from '../components/common/Modal';
import {
  AccountQuestionPanel,
  RedemptionOutcomePanel,
  RedeemProgressPanel,
  type RedemptionOutcome,
} from '../components/redeem/RedemptionPanels';
import { FocusedRedeemShell } from '../components/redeem/FocusedRedeemShell';
import { prefetchOnboardingRecommendations } from '../services/onboardingRecsPrefetch';
import { JourneyStepper, NEW_ACCOUNT_JOURNEY } from '../components/onboarding/JourneyStepper';
import {
  WU_PRICING,
  WU_FULL_NAME,
  MONTHLY_WU_PER_PRESTON,
  hireQuote,
  HireCadence,
} from '../config/workUnits';
// The commitment answers, shared with /subscription so the two cannot drift —
// until now only a paying customer could read them (plan C5.3).
import { PRICING_PAGE_FAQ, COMMITMENT_FAQ_HEADING } from '../config/commitmentFaq';
// The same code-backed trust block the home page carries (plan C6).
import { TrustBlock } from '../components/landing/Mechanisms';
import SkeletonBlock from '../components/common/SkeletonBlock';
// The five shipped roles, straight from the CI-gated template mirror.
import { rolesInOrder } from '../components/room/roomScenarios';
import { WorkCalculatorLauncher } from '../components/pricing/WorkUnitCalculator';
import ZeroMonth from '../components/zero/ZeroMonth';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '../services/prest0nSession/client';
import ShinyGoldButton from '../components/pricing/ShinyGoldButton';
import KeepCase from '../components/common/KeepCase';
import {
  useWorkspaceBillingSnapshot,
  fnUrl as wsFnUrl,
  getAuthToken as wsGetAuthToken,
} from '../components/pricing/useWorkspaceBillingSnapshot';
import { invalidateBillingSnapshot } from '../services/billingSnapshotCache';
// LP-R4 (2026-08-16): both the redeem call and the pre-auth status probe run
// through the shared client — one error map for every surface (the inline
// fetch's partial map made CODE_EXPIRED / CODE_AMOUNT_INVALID unreachable).
import {
  checkAccessCode as checkAccessCodeRequest,
  redeemAccessCode as redeemAccessCodeRequest,
} from '../services/accessCodeClient';

// ============================================================================
// Helpers
// ============================================================================

/** "$3,200" — whole-dollar display for ladder prices. */
const fmtUsd = (n: number): string => `$${n.toLocaleString('en-US')}`;


/** "2,200" — monthly WU allocation display. */
const MONTHLY_WU_DISPLAY = MONTHLY_WU_PER_PRESTON.toLocaleString('en-US');

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const inputPulse = keyframes`
  0%, 100% { border-color: #E0DED8; }
  50% { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.15); }
`;

// ============================================================================
// Navigation (matches Mission.tsx pattern)
// ============================================================================

const HomeHeader = styled.header`
  position: absolute;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  &.active {
    color: var(--brand-gold);
    background-color: rgba(198, 167, 94, 0.08);
    svg { opacity: 1; color: var(--brand-gold); }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg { opacity: 1; }
`;

const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;

  &:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }

  @media (max-width: 768px) { display: flex; }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-black);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;

    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '4px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }
    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }
    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '14px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem 0;
  z-index: 200000;
  flex-direction: column;
  width: 200px;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.25s ease-out;

  @media (max-width: 768px) { display: flex; }

  @media (max-width: 480px) {
    width: 180px;
    top: 3.5rem;
    right: 0.75rem;
    padding: 0.75rem 0;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
    svg { opacity: 1; }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
    svg { width: 18px; height: 18px; }
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;
    svg { opacity: 1; }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;
    svg { width: 18px; height: 18px; }
  }
`;

// ============================================================================
// Layout
// ============================================================================

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  /* No bottom padding — GlobalFooter is the page's last element and supplies
     its own tall bottom padding (which also clears the floating chat bar). */
  padding: 0;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
`;
const TwoColumnLayout = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  /* Dead-space pass (2026-08-14 owner screenshot): the hero used to carry
     8rem of bottom padding under a SHORT left column standing beside a tall
     card — nearly 200px of blank page between the code input and the first
     section. The rhythm is now set by the sections themselves. */
  padding: 4.75rem 2rem 2.25rem;
  display: grid;
  /* Funnel column outweighs the plan card — the invite-code entry is
     this page's primary CTA, so it gets the width on desktop
     (2026-08-03 desktop-polish audit). */
  grid-template-columns: 1.15fr 0.85fr;
  gap: 3.25rem;
  /* Vertical balance (2026-08-14 owner screenshot round 3): the funnel column
     is shorter than the plan card, and top-aligning it left ~250px of blank
     page under the quote. Centering it in the row splits that free space
     evenly above and below, so the two columns read as one composed block
     instead of a tall card beside a short one. Stacked layouts stay top-aligned. */
  align-items: center;
  box-sizing: border-box;
  width: 100%;

  @media (min-width: 1280px) {
    grid-template-columns: 1.18fr 0.82fr;
  }

  /* Wide-viewport pass (2026-08-14 round 2, owner: still too much dead
     space): on ≥1600px screens the 1240px cap left gutters wider than the
     content columns. Let the hero breathe into the width instead. */
  @media (min-width: 1600px) {
    max-width: 1400px;
    gap: 4rem;
  }

  @media (max-width: 1024px) {
    gap: 2.5rem;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 5.5rem 1.25rem 2rem;
    gap: 2rem;
    max-width: 700px;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    padding: 4.75rem 1rem 1.5rem;
    gap: 1.5rem;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;

  @media (max-width: 960px) {
    align-self: start;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Hero = styled.section`
  animation: ${fadeIn} 0.45s ease both;
  box-sizing: border-box;
  text-align: left;
`;

const Eyebrow = styled.p`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: #AAA;
  margin-bottom: 0.5rem;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: clamp(1.8rem, 5vw, 2.7rem);
  font-weight: 400;
  letter-spacing: -0.03em;
  /* Rendered lowercase in the house style while the DOM text keeps its
     natural casing for search engines and screen readers. */
  text-transform: lowercase;
  color: var(--brand-text);
  line-height: 1.15;
  margin-bottom: 0.6rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const HeroSub = styled.p`
  font-size: clamp(0.875rem, 2.5vw, 0.95rem);
  color: #666;
  line-height: 1.6;
  margin: 0 0 1rem;
  box-sizing: border-box;
  /* Measure cap (2026-08-14 round 3): the funnel column is ~750px on a wide
     screen, which ran this paragraph to ~110 characters a line — well past the
     comfortable 60–75. Capping it also gives the shorter left column a little
     more height, so the centered composition sits closer to the card's. */
  max-width: 34rem;

  @media (max-width: 960px) {
    max-width: none;
  }
`;

const HeroQuote = styled.blockquote`
  font-size: clamp(0.85rem, 2.5vw, 0.95rem);
  font-style: italic;
  color: #555;
  border-left: 2px solid var(--brand-gold);
  padding: 0 0 0 1rem;
  margin: 1.5rem 0 0;
  text-align: left;
  line-height: 1.5;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding-left: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const HeroQuoteAttribution = styled.cite`
  display: block;
  margin-top: 0.6rem;
  font-size: 0.82rem;
  font-style: normal;
  font-weight: 600;
  color: #888;
`;

// ============================================================================
// Enterprise License Card
// ============================================================================

const EnterpriseSection = styled.div`
  animation: ${fadeIn} 0.55s 0.08s ease both;
  box-sizing: border-box;
  width: 100%;
`;

const EnterpriseCard = styled.div`
  background: #fff;
  border-radius: 1.5rem;
  /* Palette unification (2026-08-14): the hard black outline was the loudest
     thing on a warm page. Brand gold at full weight keeps the card dominant
     without introducing a second color system. */
  border: 1.5px solid rgba(198, 167, 94, 0.85);
  box-shadow: 0 1rem 2.5rem rgba(120, 96, 40, 0.13);
  padding: 1.75rem 1.5rem;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    padding: 1.9rem;
  }

  @media (max-width: 480px) {
    padding: 1.4rem 1rem;
    border-radius: 1rem;
  }
`;

/* ── Workspace panel skeleton (2026-08-25 owner report: "the user sees one
   thing and it takes several seconds for them to see conflicting
   information"). Signed in, this card can resolve to three very different
   panels — the hire offer, the live plan, or the permission wall — so until
   the workspace answer is KNOWN it must assert none of them. It mirrors the
   real card's silhouette (badge → title → price → facts → button) so the
   wait reads as loading and nothing moves when the answer lands.
   Repeat visits paint the cached answer instead and never see this. ── */
// SkeletonBlock moved to src/components/common/SkeletonBlock.tsx on 2026-09-09
// so /subscription could stop showing a bare "loading…" and use the same one.

const EnterpriseBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.06em;
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(198, 167, 94, 0.5);
  color: #8A6F2F;
  padding: 4px 12px;
  border-radius: 999px;
  display: inline-block;
  margin-bottom: 0.85rem;
`;

const EnterpriseTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.25rem, 3.4vw, 1.7rem);
  font-weight: 600;
  letter-spacing: -0.025em;
  text-transform: lowercase;
  color: var(--brand-text);
  margin-bottom: 0.25rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const EnterprisePrice = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.9rem, 5vw, 2.6rem);
  font-weight: 500;
  letter-spacing: -0.04em;
  color: var(--brand-text);
  margin: 0.85rem 0 0.3rem;
  line-height: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

/** One fact per line — the old sub-line packed three separate facts into a
 *  single grey paragraph nobody parsed (2026-08-14 owner: "everything needs
 *  to be more clear"). */
const PriceFact = styled.p`
  font-size: 0.82rem;
  color: #6F6A60;
  line-height: 1.5;
  margin: 0 0 0.3rem;
  /* See FeaturesSub — lowercase look, natural-case source. */
  text-transform: lowercase;

  strong {
    color: var(--brand-text);
    font-weight: 600;
  }
`;

const PriceFactList = styled.div`
  margin-bottom: 1.25rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #F0EDE4;
  margin: 1.25rem 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0 0 1.35rem;
  padding: 0;
  display: grid;
  gap: 0.55rem;
  grid-template-columns: 1fr;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.84rem;
  color: #444;
  line-height: 1.5;
`;

/** Brand-gold check (2026-08-14 palette pass): the page carried two color
 *  systems — warm gold chrome with green confirmation dots borrowed from the
 *  app's "connected" semantics. Marketing checkmarks are decorative, so they
 *  now speak the brand palette; green stays reserved for live success states
 *  (CodeSuccess). */
const Check = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F3EAD4 0%, #FBF7EE 100%);
  color: #A9853B;
  font-size: 0.6rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const PriceUnit = styled.span`
  font-size: 0.45em;
  font-weight: 500;
  color: #888;
  letter-spacing: -0.01em;
`;

// ── The hire picker (inside the hire card) ──────────────────────────────────
//
// 2026-08-16 owner directive: never a tier table, never a comparison chart —
// the buyer toggles cadence and team size, and the price + % discount above
// recompute live. Decision fatigue is the enemy; one offer, two toggles.

const DiscountBlock = styled.div`
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(198, 167, 94, 0.4);
  border-radius: 1rem;
  padding: 0.95rem 1rem 0.85rem;
  margin-bottom: 1.25rem;
  box-sizing: border-box;
`;

const DiscountHead = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.01em;
  color: #8A6F2F;
  margin-bottom: 0.6rem;
`;

const CadenceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.7rem;
`;

const CadenceBtn = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.35)')};
  background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'transparent')};
  color: ${(p) => (p.$active ? '#FFF' : '#6B5D3F')};
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: lowercase;
  border-radius: 999px;
  padding: 0.38rem 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--brand-gold);
  }
`;

const SeatPickRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SeatBtnMini = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(198, 167, 94, 0.45);
  background: #FFF;
  color: #6B5D3F;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const SeatNumInput = styled.input`
  width: 3.2rem;
  text-align: center;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #3D3012;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 8px;
  padding: 0.28rem 0.2rem;
  background: #FFF;
  -moz-appearance: textfield;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const SeatPickLabel = styled.div`
  font-size: 0.8rem;
  color: #4A4237;
  text-transform: lowercase;
`;

const DiscountFoot = styled.div`
  font-size: 0.72rem;
  color: #8A6F2F;
  line-height: 1.5;
  margin-top: 0.6rem;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(198, 167, 94, 0.22);
`;

/** Ties the paid card back to the free invite-code path on the left, so the
 *  two halves of the hero read as one offer instead of two rival CTAs. */
const CardFootNote = styled.div`
  font-size: 0.75rem;
  color: #9A948A;
  text-align: center;
  line-height: 1.5;
  margin-top: 0.7rem;
`;

/* Quiet inline link-button for footnote actions (signed-in panel, 2026-08-21). */
const InlineLinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  min-height: 0;
  min-width: 0;
  font: inherit;
  color: #8A6F2F;
  text-transform: lowercase;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-decoration-color: rgba(198, 167, 94, 0.5);
  &:hover { color: var(--brand-gold-hover); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--brand-gold); outline-offset: 2px; border-radius: 0.2rem; }
`;

// ── Published ladder / flex / rate-card / larger-companies sections ─────────

/** One content width for every section below the hero (2026-08-14): the page
 *  used to alternate 900px and 1100px containers, which read as ragged
 *  margins — part of what the owner saw as dead space. Prose inside stays
 *  narrow via FeaturesSub. */
const PricingSection = styled.section`
  max-width: 1040px;
  margin: 4rem auto 0;
  padding: 0 1.25rem;
  animation: ${fadeIn} 0.55s 0.1s ease both;
  box-sizing: border-box;
  width: 100%;

  /* Wide-viewport pass (2026-08-14 round 2) — grow with the hero. */
  @media (min-width: 1600px) {
    max-width: 1200px;
  }
`;

/** Warm band behind a section — gives the long scroll a rhythm instead of one
 *  unbroken sheet of white. */
const SectionBand = styled.div`
  background: linear-gradient(180deg, #F6F2E9 0%, #FBF9F4 100%);
  border-top: 1px solid #EFE9DC;
  border-bottom: 1px solid #EFE9DC;
  padding: 0.25rem 0 2.25rem;
  margin-top: 2.25rem;

  /* The band supplies the rhythm — the section inside must not double it. */
  ${PricingSection} {
    margin-top: 1.9rem;
  }
`;

/* (Ladder table styled components removed 2026-08-16 — tiers never render as
   a table; SaveBadge lives on as the discount pill in the hire card.) */

/** The discount pill beside the big price. Self-contained typography: it
 *  sits inside EnterprisePrice, whose -0.04em display tracking and clamp()
 *  size squashed the inherited text into an unreadable smear (2026-08-16
 *  owner screenshot) — so family, size, and spacing are all reset here, and
 *  the pill is sized to be read first. */
const SaveBadge = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-family: 'Inter', sans-serif;
  font-size: 1.02rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1;
  color: #7A5C00;
  background: #F3EAD4;
  border: 1.5px solid rgba(198, 167, 94, 0.55);
  border-radius: 999px;
  padding: 8px 16px;
  margin-left: 0.7rem;
  white-space: nowrap;
  text-transform: lowercase;
`;












/** Brand-gold CTA (2026-08-14, carrying the onboarding directive onto the
 *  marketing surface: "the black buttons potentially look scary"). */
const PrimaryButton = styled.button`
  flex: 1;
  min-width: 0;
  background: var(--brand-gold);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 0.9rem 1.5rem;
  font-size: 0.92rem;
  font-weight: 600;
  font-family: inherit;
  text-transform: lowercase;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: background 0.15s ease, transform 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover { background: var(--brand-gold-hover); transform: translateY(-1px); }
`;

const SecondaryButton = styled.a`
  flex: 1;
  min-width: 0;
  background: #fff;
  color: var(--brand-text);
  border: 1.5px solid var(--brand-text);
  border-radius: 0.85rem;
  padding: 0.9rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: background 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover { background: #F5F5F5; }
`;

// ============================================================================
// Access Code Section
// ============================================================================

/** The invite-code entry is this page's primary action, so it now reads as a
 *  deliberate "start here" card rather than loose text under the hero
 *  (2026-08-14 owner: the code block and the five steps were "awkwardly
 *  placed"). Kept as a section element for the existing scroll ref. */
const AccessCodeSection = styled.section`
  margin: 1.5rem 0 0;
  padding: 1.4rem 1.35rem 1.25rem;
  background: #fff;
  border: 1px solid #E7DCC3;
  border-radius: 1.25rem;
  box-shadow: 0 0.5rem 1.5rem rgba(120, 96, 40, 0.07);
  animation: ${fadeIn} 0.55s 0.06s ease both;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 480px) {
    padding: 1.15rem 1rem 1rem;
    border-radius: 1rem;
  }
`;

const AccessCodeEyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: var(--brand-gold-hover);
  margin-bottom: 0.35rem;
`;

const AccessCodeHeading = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.05rem, 1.6vw, 1.3rem);
  font-weight: 600;
  color: var(--brand-text);
  text-transform: lowercase;
  margin-bottom: 0.3rem;
  letter-spacing: -0.01em;
`;

const AccessCodeHelp = styled.p`
  font-size: 0.82rem;
  color: #7A746A;
  line-height: 1.55;
  margin-bottom: 0.9rem;

  @media (min-width: 1024px) {
    font-size: 0.86rem;
  }
`;

// ── the five-step journey, as chips ─────────────────────────────────────────
//
// Same steps, same count, same labels as the in-flow JourneyStepper (both
// read NEW_ACCOUNT_JOURNEY — the count can never drift), but drawn for a
// marketing card: chips wrap gracefully at any width, where the connector
// rail stretched into dangling stubs when it wrapped mid-flow in this column
// (2026-08-14 owner screenshot).

const JourneyIntro = styled.div`
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0.02em;
  color: #9A948A;
  margin-bottom: 0.5rem;
`;

const JourneyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const JourneyChip = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.7rem 0.32rem 0.35rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: ${p => (p.$active ? 700 : 500)};
  text-transform: lowercase;
  white-space: nowrap;
  background: ${p => (p.$active ? 'var(--brand-gold)' : '#FBFAF7')};
  color: ${p => (p.$active ? '#fff' : '#857F73')};
  border: 1px solid ${p => (p.$active ? 'var(--brand-gold)' : '#EAE5DA')};
`;

const ChipNum = styled.span<{ $active?: boolean }>`
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  background: ${p => (p.$active ? 'rgba(255, 255, 255, 0.28)' : '#EFEBE1')};
  color: ${p => (p.$active ? '#fff' : '#9A948A')};
`;

const ChipOptional = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: #B3ADA1;
  text-transform: lowercase;
`;

const CodeInputRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: stretch;

  @media (max-width: 400px) {
    flex-direction: column;
  }
`;

const CodeInput = styled.input<{ $pulsed?: boolean }>`
  flex: 1;
  /* Wrap pass (2026-08-09): without min-width, flex refuses to shrink below
     content size and the 401-480px viewport row pinches (StartTrial's
     BigCodeInput precedent). */
  min-width: 0;
  border: 1.5px solid #E0DED8;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  font-family: 'Space Grotesk', 'Inter', monospace;
  letter-spacing: 0.04em;
  text-transform: lowercase;
  background: #fff;
  color: var(--brand-text);
  outline: none;
  transition: border-color 0.15s ease;
  ${props => props.$pulsed ? css`animation: ${inputPulse} 1s ease 0.3s 2;` : ''}

  &::placeholder { color: #BBB; text-transform: none; }
  &:focus { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.16); }
`;

const CodeButton = styled.button`
  background: var(--brand-gold);
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.35rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  text-transform: lowercase;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover { background: var(--brand-gold-hover); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const CodeError = styled.div`
  margin-top: 0.6rem;
  color: #B42318;
  font-size: 0.8rem;
  line-height: 1.5;
`;

const CodeSuccess = styled.div`
  margin-top: 0.6rem;
  color: #067647;
  font-size: 0.8rem;
  line-height: 1.5;
`;

// ============================================================================
// Full Capabilities — everything your AI employee can do
// ============================================================================

const FeaturesSection = styled.section`
  /* Same width token as PricingSection — the capability grid used to be the
     one 1100px section on a 1040px page (ragged margins). */
  max-width: 1040px;
  margin: 1rem auto 0;
  padding: 0 1.25rem 1rem;
  animation: ${fadeIn} 0.55s 0.12s ease both;
  box-sizing: border-box;
  width: 100%;

  @media (min-width: 1600px) {
    max-width: 1200px;
  }
`;

const FeaturesTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.35rem, 3.6vw, 1.75rem);
  font-weight: 600;
  letter-spacing: -0.025em;
  /* One voice across the page (2026-08-14 owner: same design language,
     lowercase). Section headings were the last Title Case holdouts. */
  text-transform: lowercase;
  color: var(--brand-text);
  text-align: center;
  margin-bottom: 0.45rem;
`;

/* The calculator trigger sits where the rate table used to — centered, quiet,
   one line (2026-08-19 owner: "subtle pop up ... not too much text"). */
const CalculatorRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.1rem;
`;

/* ── Value-prop band pieces (2026-08-19 owner directive) ────────────────── */

/* The innovation list, one claim + a plain-words gloss each. Bare strings on
 * purpose: the guest knowledge extractor picks up quoted array strings, so the
 * signed-out bot can answer "what makes prest0 different". Keep them free of
 * semicolons/braces (extractor rejects code-looking text) and free of the
 * pinned-forbidden dollar figures. */
const INNOVATIONS = [
  'every task runs on the model chosen for that task, not one default',
  'retries, overruns and thinking time are always free — you pay for finished work',
  'correct it once and it holds — it consolidates what it learned every night',
  'it reads whole batches of files and comes back with one deliverable',
  'research comes back cited, as PDF and Word',
  'anything that leaves your firm waits for a person to authorize it',
  'permissions are checked for the person asking, on every single message',
  'improvements land in your plan automatically',
];

const InnovationToggleRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.1rem;
`;

const InnovationToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  min-height: 0;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: #8A6F2F;
  text-transform: lowercase;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-decoration-color: rgba(198, 167, 94, 0.5);

  &:hover { color: var(--brand-gold-hover); text-decoration-color: var(--brand-gold-hover); }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--brand-gold); outline-offset: 3px; border-radius: 0.2rem; }
`;

const InnovationGrid = styled.ul`
  list-style: none;
  margin: 1.4rem auto 0;
  padding: 0;
  max-width: 780px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.55rem;
  animation: ${fadeIn} 0.35s ease both;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const shineSweepTile = keyframes`
  0%, 70% { transform: translateX(-140%) skewX(-18deg); }
  100% { transform: translateX(280%) skewX(-18deg); }
`;

/* The headline innovation (2026-08-21 owner directive): the patented memory
 * system gets the one shiny gold tile, full-width at the top of the grid. */
const FeaturedInnovation = styled.li`
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.45;
  color: #2B2208;
  text-transform: lowercase;
  text-align: center;
  background: linear-gradient(135deg, #EAD394 0%, var(--brand-gold) 48%, var(--brand-gold-hover) 100%);
  border: 1px solid rgba(198, 167, 94, 0.6);
  border-radius: 0.85rem;
  padding: 0.85rem 1.1rem;
  box-shadow: 0 8px 24px rgba(198, 167, 94, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.45);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 42%;
    background: linear-gradient(105deg, transparent 0%, rgba(255, 255, 255, 0.45) 50%, transparent 100%);
    animation: ${shineSweepTile} 4.6s ease-in-out infinite;
    pointer-events: none;
  }
  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; }
  }
`;

const InnovationChip = styled.li`
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(200, 167, 94, 0.35);
  border-radius: 0.75rem;
  padding: 0.65rem 0.9rem;
  text-align: left;
`;

/* Signup CTA row inside the access card (primary conversion action). */
const SignupCtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.9rem;
`;

/* Quiet lead-in for the demoted invitation-code path. */
const CodeLead = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  color: #6F6A60;
  text-transform: lowercase;
`;

const FeaturesSub = styled.p`
  font-size: 0.9rem;
  color: #6F6A60;
  line-height: 1.65;
  /* Rendered lowercase, written in natural case (2026-08-14 CI catch): the
     guest knowledge base and screen readers are generated from the DOM text,
     so product names like "PRO Work Units" must stay properly capitalized in
     the source even though the page renders them lowercase. */
  text-transform: lowercase;
  text-align: center;
  max-width: 640px;
  margin: 0 auto 1.75rem;
`;

/* Task #70: the ladder's click-to-open control — quiet, centered, gold. */
/* Seven capability groups of unequal length. A CSS grid gave them equal-height
   rows with an empty trailing slot (2026-08-14 round 3 screenshot: a hole where
   an eighth card would be, plus 60–90px of blank card interior under the short
   lists). Wrapping flex fixes both — the final row centers itself under the
   full ones, and `align-items: flex-start` lets every card stop at its own
   content instead of stretching to its neighbour. */
/* Eight capability groups, so the block tiles exactly at every width it is
   shown in: one column on phones, two on tablets (4 rows), four on desktop
   (2 rows). Three columns is deliberately skipped — 8 into 3 leaves the same
   trailing hole seven cards used to leave, and a masonry column flow traded
   that hole for a column running ~165px past its neighbours. */
const GroupGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const GroupCard = styled.div`
  background: #fff;
  border: 1px solid #EBEBEA;
  border-radius: 1rem;
  padding: 1.25rem 1.25rem 1rem;
  box-sizing: border-box;
`;

const GroupName = styled.h3`
  text-transform: lowercase;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--brand-text);
  letter-spacing: -0.01em;
  margin-bottom: 0.85rem;
`;

const FeatRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.22rem 0;
`;

const FeatName = styled.div`
  font-size: 0.84rem;
  color: #333;
  line-height: 1.45;
`;

const TrainSection = styled.div`
  margin-top: 2rem;
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 1.25rem;
  padding: 1.75rem 1.5rem;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 2rem 2.25rem;
  }
`;

const TrainTitle = styled.h3`
  text-transform: lowercase;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.15rem, 3.5vw, 1.5rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #3D3012;
  margin-bottom: 0.75rem;
`;

const TrainLead = styled.p`
  font-size: 0.88rem;
  color: #5E4A1A;
  line-height: 1.7;
  max-width: 760px;
  margin-bottom: 1.25rem;
`;

const TrainGrid = styled.div`
  display: grid;
  gap: 0.45rem 1.25rem;
  grid-template-columns: 1fr;

  @media (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TrainItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: #3D3012;
  line-height: 1.5;
`;

// ============================================================================
// Social Proof — DELETED 2026-09-11
//
// SocialProofSection, SocialProofTitle, TestimonialGrid, TestimonialCard,
// QuoteText and Attribution were declared here and rendered NOWHERE: an empty
// three-card testimonial frame, styled and ready, with nothing in it.
//
// A frame like that is an invitation to fill it under deadline, and testimonial
// copy written to fit a layout is exactly what 16 CFR 465 is about. The real
// attributed quote this page carries (Fernando Hidalgo, a real customer, with
// his name on it) lives in the hero and is untouched. Git history is the
// archive; a future testimonial section should be built alongside its sourcing
// record in config/marketing-claims.json, not resurrected from here.
// ============================================================================

// ============================================================================
// FAQ
// ============================================================================

const FAQSection = styled.section`
  max-width: 700px;
  margin: 2.5rem auto 0;
  padding: 0 1.25rem;
  animation: ${fadeIn} 0.55s 0.15s ease both;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }
`;

const FAQTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 1.25rem;
  letter-spacing: -0.01em;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FAQItem = styled.div`
  border-top: 1px solid #EBEBEA;
  padding: 1rem 0;

  &:last-child { border-bottom: 1px solid #EBEBEA; }
`;

const FAQQuestion = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.4rem;
`;

const FAQAnswer = styled.div`
  font-size: 0.83rem;
  line-height: 1.65;
  color: #666;
`;

// ============================================================================
// Capability data — canonical feature inventory (2026-07, invite-only revision;
// names only — the page stays scannable, the inventory stays complete)
// ============================================================================

const CAPABILITY_GROUPS: Array<{ group: string; features: string[] }> = [
  {
    group: 'Research & Analysis',
    features: [
      'Deep Research with citations',
      'Live web & market research',
      'Regulatory & compliance research',
      'Meeting prep & briefings',
    ],
  },
  {
    group: 'Documents & Drafting',
    features: [
      'Document drafting in your voice',
      'Contract review',
      'Automatic form filling',
      'Branded PDF & DOCX exports',
      'Video scripts',
    ],
  },
  {
    /* Split out of Documents & Drafting (2026-08-14 layout round 3). That group
       carried nine items against neighbours of four, which no column count
       could tile — one card ran twice its row's height and left a hole where an
       eighth would sit. Same capabilities, grouped by what they act on:
       drafting produces documents, this reads and processes them. */
    group: 'Files & Data',
    features: [
      'Document reading & analysis',
      `Multi-file processing (${MAX_ATTACHMENTS_PER_MESSAGE} at once)`,
      'Spreadsheets & data work',
      'Instant translation (EN ↔ ES)',
    ],
  },
  {
    group: 'Communication',
    features: [
      'AI voice calls',
      'Two-way SMS',
      'Email management',
      'Calendar & scheduling',
      'Unified communication hub',
      'Proactive outreach & follow-ups',
      '24/7 bilingual client intake',
    ],
  },
  {
    group: 'Automation & Integrations',
    features: [
      'Multi-step workflow orchestration',
      'Integrates with any system',
      'Live browser agent',
      'In-chat payments',
      'Website chat widget',
      'Deadline & task tracking',
    ],
  },
  {
    group: 'Memory & Training',
    features: [
      'Persistent institutional memory',
      'Overnight learning',
      'Cross-conversation search',
      'Learns your voice & templates',
    ],
  },
  {
    group: 'Team & Workspace',
    features: [
      'Shared group threads',
      'Unlimited parallel work',
      'Workspaces & invitations',
      'Roles & permissions',
      'Usage & budget controls',
    ],
  },
  {
    group: 'Security & Privacy',
    features: [
      'Encrypted & private by design',
      'Human approval guardrails',
      'Full audit trail',
      'Enterprise SLA & support',
      'Unlimited users',
    ],
  },
];

const TRAINABLE_CAPABILITIES: string[] = [
  'Custom specialist agents',
  'Custom workflow automation',
  'Client self-service portal',
  'Custom tool development',
  'Operations & revenue analytics',
  'Client intake automation',
  'Competitive intelligence',
  'Multi-office coordination',
  'Training & onboarding content',
  'Industry workflow packs',
  'White-label branding',
  'Named agent personality',
];

// ============================================================================
// Published pricing data — presentation order only; every number comes from
// src/config/workUnits.ts (the single source of truth)
// ============================================================================

// (LADDER_RUNGS removed 2026-08-16 — the ladder table is gone; the hire
// picker in the card is the only rate explorer.)

// (FLEX_OPTIONS removed 2026-08-16 — the work-spikes section is gone; the
// no-surprise-billing promise lives in the hire card.)

/**
 * The page's meta description, DERIVED rather than typed.
 *
 * It used to be a string literal carrying "2,200 PRO Work Units", "$3,200/mo"
 * and "20% off" by hand. The page body renders every one of those from
 * src/config/workUnits.ts and so cannot drift, but this line sat outside that
 * guarantee and outside check-public-pricing-sync.mjs (which scans public/*,
 * not src/*) — so a ladder change would have left the search result and the
 * social card quoting a price the page no longer offered.
 */
const PRICING_META_DESCRIPTION = [
  `published pricing: a Prest0n professional — a full-time digital employee with`,
  `${MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} ${WU_FULL_NAME} per month —`,
  `from $${WU_PRICING.annual.monthlyUsd.toLocaleString('en-US')}/mo on an annual hire.`,
  `hire more, pay less automatically; prepay the year for`,
  `${WU_PRICING.prepayDiscountPct}% off. identical for everyone; free to start.`,
].join(' ');

// ============================================================================
// Component
// ============================================================================

const Pricing: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Access code state
  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSuccess, setCodeSuccess] = useState(false);
  const [codeSuccessMessage, setCodeSuccessMessage] = useState('Code accepted! Setting up your workspace…');
  const [prefilledFromUrl, setPrefilledFromUrl] = useState(false);
  const [returnedFromAuth, setReturnedFromAuth] = useState(false);
  // Signed-out + valid unused code → ask whether they already have an account
  const [accountQuestionCode, setAccountQuestionCode] = useState<string | null>(null);
  // Signed-in wallet-credit / trial-extension confirmations (no onboarding)
  const [redeemOutcome, setRedeemOutcome] = useState<RedemptionOutcome | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [innovationsOpen, setInnovationsOpen] = useState(true);
  // The hire picker (2026-08-16): cadence + team size drive the one price shown.
  const [hireCadence, setHireCadence] = useState<HireCadence>('annual');
  const [hireSeats, setHireSeats] = useState(1);

  // ── Signed-in workspace panel (2026-08-21 owner directive) ─────────────
  // The right column stops pitching "create your account" to people who have
  // one: the same resolution the subscription page runs decides between the
  // tailored offer, the live plan (with management), or the permission wall.
  // The caller's own uid is passed in deliberately: AuthContext restores it
  // from storage in the FIRST frame, while firebase restores its session
  // asynchronously — that is what lets a repeat visit paint the real answer
  // immediately instead of a pitch it has to take back (2026-08-25).
  const ws = useWorkspaceBillingSnapshot(isAuthenticated, user?.id);
  // A live plan seeds the picker ONCE, turning it into the adjuster —
  // exactly the seeding rule the subscription page's AdjustBox uses.
  const wsSeededRef = useRef(false);
  useEffect(() => {
    if (ws.kind !== 'plan' || wsSeededRef.current) return;
    wsSeededRef.current = true;
    if (ws.term) setHireCadence(ws.term);
    if (ws.seats) setHireSeats(ws.seats);
  }, [ws]);
  const wsPlanDirty = ws.kind === 'plan' && (hireCadence !== ws.term || hireSeats !== ws.seats);
  // Which of the three signed-in panels the card renders. Named rather than
  // inlined into the JSX ternary on purpose: the guest-knowledge generator
  // scrapes text between JSX tags, and a long inline condition sitting
  // between `</>` and the next tag reads to it as page copy — that is how
  // raw code leaked into knowledgeSource.generated.json.
  const wsAnswerPending = isAuthenticated && ws.kind === 'loading';
  const wsShowsWall = isAuthenticated && (ws.kind === 'view' || ws.kind === 'wall');
  const [wsChange, setWsChange] = useState<
    | { phase: 'idle' }
    | { phase: 'preview'; line: string }
    | { phase: 'applying' }
    | { phase: 'applied'; line: string }
    | { phase: 'failed' }
  >({ phase: 'idle' });
  // Debounced preview — same changeSubscription {mode:'preview'} contract the
  // subscription page's adjuster calls; copy mirrors its two outcomes.
  useEffect(() => {
    if (ws.kind !== 'plan' || !ws.canManage || !wsPlanDirty) { setWsChange({ phase: 'idle' }); return undefined; }
    let canceled = false;
    const t = setTimeout(async () => {
      try {
        const token = await wsGetAuthToken();
        if (!token || canceled) return;
        const resp = await fetch(wsFnUrl('changeSubscription'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            mode: 'preview', term: hireCadence, seats: hireSeats,
            ...(ws.accountId ? { accountId: ws.accountId } : {}),
          }),
        });
        const data = resp.ok ? await resp.json() : null;
        if (canceled || !data) return;
        const line = data.effectiveAt === 'now'
          ? `applies immediately${typeof data.amountDueNowCents === 'number' && data.amountDueNowCents > 0 ? ` — about ${fmtUsd(data.amountDueNowCents / 100)} prorated to your card on file` : ''}`
          : 'nothing charged today — this takes effect when your commitment is up';
        setWsChange({ phase: 'preview', line });
      } catch { /* preview is advisory — apply still confirms server-side */ }
    }, 500);
    return () => { canceled = true; clearTimeout(t); };
  }, [ws, wsPlanDirty, hireCadence, hireSeats]);
  const applyWsChange = async () => {
    if (ws.kind !== 'plan' || !ws.canManage || !wsPlanDirty) return;
    setWsChange({ phase: 'applying' });
    // The cached seats/term stop describing this workspace the moment the
    // apply goes out — drop it so /subscription cannot paint the old plan.
    invalidateBillingSnapshot(user?.id);
    try {
      const token = await wsGetAuthToken();
      if (!token) throw new Error('no-auth');
      const resp = await fetch(wsFnUrl('changeSubscription'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          mode: 'apply', term: hireCadence, seats: hireSeats,
          ...(ws.accountId ? { accountId: ws.accountId } : {}),
        }),
      });
      const data = resp.ok ? await resp.json() : null;
      if (!data) throw new Error('apply-failed');
      if (data.needsCheckout) {
        // A change that needs a fresh checkout runs on the subscription page,
        // where the full card machinery lives.
        navigate(ws.accountId ? `/subscription?account=${ws.accountId}` : '/subscription');
        return;
      }
      setWsChange({
        phase: 'applied',
        line: data.effectiveAt === 'now'
          ? 'done — your change is live'
          : 'done — scheduled for the end of your current term',
      });
    } catch {
      setWsChange({ phase: 'failed' });
    }
  };
  const [wsRequest, setWsRequest] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const sendWsBillingRequest = async (kind: 'hire' | 'plan_change') => {
    if (!ws.accountId) return;
    setWsRequest('sending');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fn = httpsCallable(getFunctions(getApp()), 'requestBillingAttention');
      await fn({ accountId: ws.accountId, kind });
      setWsRequest('sent');
    } catch {
      setWsRequest('failed');
    }
  };
  const accessCodeRef = useRef<HTMLElement>(null);

  // Pre-fill code from URL param and auto-scroll to it
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefilledCode = params.get('code');
    if (prefilledCode) {
      setCodeInput(prefilledCode.toUpperCase());
      setPrefilledFromUrl(true);
      setTimeout(() => {
        accessCodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 350);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('code') && isAuthenticated) {
      setReturnedFromAuth(true);
    }
  }, [isAuthenticated, location.search]);

  // Kill the marketing-page flash on auth return (2026-08-09 owner
  // directive) — same shape as StartTrial: initialize synchronously from the
  // signals the auto-redeem effect consumes so the FIRST commit renders the
  // focused shell, with a safety timeout and a hand-off to the live
  // redemption state (see StartTrial.tsx for the full rationale).
  const [authReturnBoot, setAuthReturnBoot] = useState(() => {
    try {
      const hasCode = new URLSearchParams(window.location.search).has('code');
      return hasCode && Boolean(sessionStorage.getItem('pending_trial_code'));
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!authReturnBoot) return;
    const t = setTimeout(() => {
      if (!isAuthenticated) setAuthReturnBoot(false);
    }, 8000);
    return () => clearTimeout(t);
  }, [authReturnBoot, isAuthenticated]);
  useEffect(() => {
    if (codeLoading && authReturnBoot) setAuthReturnBoot(false);
  }, [codeLoading, authReturnBoot]);

  // Auto-redeem: fires once when the user returns from account creation with a
  // pending code (set in handleRedeemCode before navigating to /auth). Cleared
  // immediately on use so page refreshes don't re-trigger.
  useEffect(() => {
    if (!isAuthenticated) return;
    let pendingCode: string | null = null;
    try {
      pendingCode = sessionStorage.getItem('pending_trial_code');
      if (pendingCode) sessionStorage.removeItem('pending_trial_code');
    } catch {}
    if (!pendingCode) return;
    const code = pendingCode.toUpperCase();
    setCodeInput(code);
    redeemCode(code);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = () => {
    navigate('/auth', { state: { returnUrl: '/pricing' } });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('button[data-mobile-toggle="true"]')
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Core redemption logic (called once the user is signed in) ──
  // Shared-client contract: never throws — every failure (missing session,
  // network trouble, typed server errors incl. CODE_EXPIRED /
  // CODE_AMOUNT_INVALID, and the masked-hint CODE_REDEEMED_BY_OTHER) arrives
  // as a ready-to-render message.
  const redeemCode = async (code: string) => {
    setCodeLoading(true);
    setCodeError(null);
    // A redemption can mint a trial subscription or credit — either way the
    // cached billing answer is out of date.
    invalidateBillingSnapshot(user?.id);

    try {
      const result = await redeemAccessCodeRequest(code);

      if (!result.success) {
        setCodeError(result.message);
        return;
      }

      // Route by redemption mode (see functions/src/accessCodeRedemption.ts)
      if (result.mode === 'wallet_credit') {
        setRedeemOutcome({ mode: 'wallet_credit', creditCents: result.creditAmountCents ?? 0 });
        return;
      }
      if (result.mode === 'trial_extension') {
        setRedeemOutcome({
          mode: 'trial_extension',
          creditCents: result.creditAddedCents ?? 0,
          expiresAt: result.expiresAt ?? null,
        });
        return;
      }

      setCodeSuccessMessage(
        result.returning
          ? 'Welcome back! Taking you to your workspace…'
          : 'Code accepted! Setting up your workspace…'
      );
      setCodeSuccess(true);
      // Start the firm-aware recommendation generation NOW, in parallel with
      // the redirect + onboarding bootstrap — the profile page's fetch then
      // hits the warm server cache instead of waiting 10-15s (2026-08-04
      // owner feedback).
      prefetchOnboardingRecommendations();
      if (result.returning) {
        setTimeout(() => navigate('/desktop'), 1200);
      } else {
        setTimeout(() => navigate('/onboarding'), 1200);
      }
    } finally {
      setCodeLoading(false);
    }
  };

  // ── Button handler: check code status, route to the right auth step ──
  const handleRedeemCode = async () => {
    const trimmedCode = codeInput.trim().toUpperCase();
    if (!trimmedCode) {
      setCodeError('Please enter an access code.');
      return;
    }

    // Already signed in → redeem directly
    if (isAuthenticated) {
      await redeemCode(trimmedCode);
      return;
    }

    // Not signed in — check whether the code is new (create account) or
    // already activated (sign in), then send them to the right step.
    await routeSignedOutCode(trimmedCode);
  };

  // Signed-out code routing — shared by the button handler and the one-click
  // invite-link auto-advance. checkAccessCode is READ-ONLY: opening a shared
  // link can never consume a code.
  const routeSignedOutCode = async (trimmedCode: string) => {
    setCodeLoading(true);
    setCodeError(null);

    try {
      // Shared client (never throws) — typed failures include the expiry
      // pre-check (an expired unused code errors here instead of funneling
      // the visitor into account creation toward a guaranteed dead end).
      const check = await checkAccessCodeRequest(trimmedCode);

      if (!check.success) {
        setCodeError(check.message);
        return;
      }

      const returnUrl = `/pricing?code=${encodeURIComponent(trimmedCode)}`;

      if (check.status === 'unused') {
        // Valid new code, signed out → ask whether they already have an
        // account first, so the code applies to the RIGHT account. The
        // auto-redeem flag is set at COMMIT time (the panel's sign-in /
        // create-account handlers), not here — a user who backs out must
        // not leave a stale pending_trial_code behind (2026-08-02 review).
        setAccountQuestionCode(trimmedCode);
      } else {
        // Already activated (attachedToExistingAccount) → sign back in with a
        // NEUTRAL hint: the pre-auth probe no longer discloses a masked email
        // (LP-R5) — the hint appears only after auth, on the redeem-time
        // CODE_REDEEMED_BY_OTHER response. Navigating now = committed.
        try { sessionStorage.setItem('pending_trial_code', trimmedCode); } catch {}
        navigate('/auth', {
          state: {
            returnUrl,
            authMode: 'login',
            loginHint: 'This code is already linked to an account. Sign in to continue.',
          },
        });
      }
    } finally {
      setCodeLoading(false);
    }
  };

  // ── One-click invite links (2026-08-02, owner directive) ──
  // A shared prest0.ai/pricing?code=XXX link auto-advances a SIGNED-OUT
  // visitor straight into the focused account-question step — zero typing,
  // zero extra clicks. Signed-in users keep prefill + an explicit apply
  // click so a forwarded link can never burn a code on the wrong account.
  // The short delay lets a restoring signed-in session settle first (the
  // effect re-runs and bails when auth flips); an in-flight auth-return
  // (pending_trial_code) is owned by the auto-redeem effect above.
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkCode = (params.get('code') || '').trim().toUpperCase();
    if (!linkCode || autoAdvancedRef.current || isAuthenticated) return;
    let authReturnInFlight = false;
    try { authReturnInFlight = Boolean(sessionStorage.getItem('pending_trial_code')); } catch {}
    if (authReturnInFlight) return;
    const t = setTimeout(() => {
      autoAdvancedRef.current = true;
      void routeSignedOutCode(linkCode);
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isAuthenticated]);

  const handleCodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRedeemCode();
  };

  return (
    <Page>
      <SeoHead
        title="Pricing — prest0 | Hire an AI Employee You Train Up"
        description={PRICING_META_DESCRIPTION}
        canonicalUrl="https://prest0.ai/pricing"
        keywords="prest0 pricing, AI employee, hire an AI employee, trainable AI assistant, enterprise AI license, AI for law finance, work units"
      />
      <HomeHeader>
        
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/mission">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
            <line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          mission
        </HeaderIconLink>
        <HeaderIconLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6"/>
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z"/>
            <line x1="9" y1="22" x2="15" y2="22"/>
          </svg>
          insights
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </HeaderIconLink>
        {isAuthenticated ? (
          <>
            <NotificationsBell />
            <ProfileImage size={36} />
          </>
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </HeaderIconButton>
        )}
      </HomeHeader>

      <MobileMenuToggle
        onClick={() => setMenuOpen(!menuOpen)}
        data-mobile-toggle="true"
        aria-label="Toggle menu"
      >
        <MobileMenuIcon isOpen={menuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>

      <MobileMenuDropdown isOpen={menuOpen} ref={mobileMenuRef}>
        
        <MobileMenuLink to="/team" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </MobileMenuLink>
        <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
            <line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          mission
        </MobileMenuLink>
        <MobileMenuLink to="/insights" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6"/>
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z"/>
            <line x1="9" y1="22" x2="15" y2="22"/>
          </svg>
          insights
        </MobileMenuLink>
        <MobileMenuLink to="/" onClick={() => setMenuOpen(false)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </MobileMenuLink>

        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M16 12h.01"/><path d="M2 10h20"/>
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="M16 12h.01"/>
                  <path d="M2 10h20"/>
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/profile" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              profile
            </MobileMenuLink>
            <MobileMenuLink to="/settings" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              settings
            </MobileMenuLink>
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
            <MobileMenuButton onClick={() => logout()} style={{ color: '#ff4d4f' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              logout
            </MobileMenuButton>
          </>
        ) : (
          <MobileMenuButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

      <TwoColumnLayout>
        <LeftColumn>
          {/* Hero */}
          <Hero>
            <Eyebrow><KeepCase>Prest0n</KeepCase> — a real ai employee, not a chatbot</Eyebrow>
            <HeroTitle>
              Hire an AI employee you train up
            </HeroTitle>
          </Hero>

          {/* Access Code — invite-only entry (or mode-based follow-up panels) */}
          <AccessCodeSection ref={accessCodeRef}>
        {redeemOutcome ? (
          <FocusedRedeemShell>
            <RedemptionOutcomePanel
              outcome={redeemOutcome}
              onGoToWorkspace={() => navigate('/desktop')}
              onGoToWallet={() => navigate('/wallet')}
            />
          </FocusedRedeemShell>
        ) : accountQuestionCode ? (
          <FocusedRedeemShell
            rail={<JourneyStepper currentKey="account" railOnDesktop />}
            onBack={() => {
            try { sessionStorage.removeItem('pending_trial_code'); } catch {}
            setAccountQuestionCode(null);
          }}>
            <AccountQuestionPanel
              onSignIn={() => {
                try { sessionStorage.setItem('pending_trial_code', accountQuestionCode); } catch {}
                navigate('/auth', {
                  state: {
                    returnUrl: `/pricing?code=${encodeURIComponent(accountQuestionCode)}`,
                    authMode: 'login',
                  },
                });
              }}
              onCreateAccount={() => {
                try { sessionStorage.setItem('pending_trial_code', accountQuestionCode); } catch {}
                navigate('/auth', {
                  state: {
                    returnUrl: `/pricing?code=${encodeURIComponent(accountQuestionCode)}`,
                    authMode: 'signup',
                  },
                });
              }}
            />
          </FocusedRedeemShell>
        ) : (((codeLoading || codeSuccess) && isAuthenticated)
          || (authReturnBoot && !codeError && !codeSuccess && !codeLoading)) ? (
          /* Post-auth trial activation stays inside the focused journey
             (2026-08-02 review): the auto-redeem lands here, so the user
             sees one continuous flow instead of dropping back onto the
             marketing page mid-setup. Gate widened 2026-08-09 to also cover
             manual signed-in redemption and the first paint after auth
             return (see StartTrial.tsx for the boot-flag rationale). */
          <FocusedRedeemShell
            rail={<JourneyStepper currentKey={codeSuccess ? 'profile' : 'account'} railOnDesktop />}
          >
            <RedeemProgressPanel stage={codeSuccess ? 'applied' : 'applying'} />
          </FocusedRedeemShell>
        ) : (
        <>
        {/* 2026-08-21 owner revision: the demo path leads THIS card (buttons
            swapped with the hire card — create-account now closes the priced
            card on the right). No sub-paragraph: the button and the code path
            say it all. */}
        <AccessCodeEyebrow>start here</AccessCodeEyebrow>
        <AccessCodeHeading>hire your first AI employee today</AccessCodeHeading>
        <SignupCtaRow>
          <ShinyGoldButton
            type="button"
            $size="lg"
            onClick={(e) => { e.preventDefault(); setIsBookingModalOpen(true); }}
          >
            book a demo — start with more <span aria-hidden="true">→</span>
          </ShinyGoldButton>
        </SignupCtaRow>

        <Divider style={{ margin: '1.15rem 0 0.85rem' }} />
        <CodeLead>
          have an invitation code? redeem it here.
        </CodeLead>

        <CodeInputRow>
          <CodeInput
            type="text"
            placeholder="PREST0-XXXX-XXXX"
            value={codeInput}
            onChange={(e) => {
              setCodeInput(e.target.value.toUpperCase());
              setCodeError(null);
            }}
            onKeyDown={handleCodeKeyDown}
            disabled={codeLoading || codeSuccess}
            $pulsed={prefilledFromUrl && !!codeInput}
            aria-label="Access code"
          />
          <CodeButton
            onClick={handleRedeemCode}
            disabled={codeLoading || codeSuccess || !codeInput.trim()}
          >
            {codeLoading ? 'Checking…' : codeSuccess ? 'Redirecting…' : 'Start Trial'}
          </CodeButton>
        </CodeInputRow>

        {codeError && <CodeError>{codeError}</CodeError>}
        {codeSuccess && (
          <CodeSuccess>{codeSuccessMessage}</CodeSuccess>
        )}
        {returnedFromAuth && !codeSuccess && !codeError && !codeLoading && (
          <CodeSuccess>account ready — activating your workspace…</CodeSuccess>
        )}

        {/* What happens after "start trial" — the canonical five steps, drawn
            as chips so they never wrap into dangling connector stubs the way
            the in-flow rail did in this column (2026-08-14 owner screenshot).
            Labels and count come straight from NEW_ACCOUNT_JOURNEY, so this
            can never disagree with the stepper the user meets next. */}
        <Divider style={{ margin: '1.1rem 0 0.9rem' }} />
        <JourneyIntro>what happens next — about two minutes</JourneyIntro>
        <JourneyRow>
          {NEW_ACCOUNT_JOURNEY.map((step, i) => {
            const optional = step.timeHint?.startsWith('optional');
            return (
              <JourneyChip key={step.key} $active={i === 0}>
                <ChipNum $active={i === 0}>{i + 1}</ChipNum>
                {step.label}
                {optional && <ChipOptional>optional</ChipOptional>}
              </JourneyChip>
            );
          })}
        </JourneyRow>
        </>
        )}
          </AccessCodeSection>

          {/* Social proof sits UNDER the code card (2026-08-14): it moves the
              primary CTA higher in the column and fills the space the shorter
              left column used to leave beside the taller plan card. */}
          <HeroQuote>
            "I'm absolutely blown away at how this initial product alone is going to change the industry."
            <HeroQuoteAttribution>— Fernando Hidalgo, Esq.</HeroQuoteAttribution>
          </HeroQuote>
        </LeftColumn>

        <RightColumn>
          {/* Prest0n Professional hire card — published pricing */}
          <EnterpriseSection>
        <EnterpriseCard>
          {wsAnswerPending ? (
            /* ── Answer not known yet: assert nothing. Showing the hire pitch
                  here is what made a paying subscriber read "hire now" for a
                  few seconds before it flipped to their own plan. ── */
            <>
              <SkeletonBlock $h="1.35rem" $w="9rem" $r="999px" />
              <SkeletonBlock $h="1.9rem" $w="85%" $mt="0.9rem" />
              <SkeletonBlock $h="2.6rem" $w="60%" $mt="0.85rem" />
              <SkeletonBlock $h="0.85rem" $mt="1.1rem" />
              <SkeletonBlock $h="0.85rem" $w="78%" $mt="0.5rem" />
              <SkeletonBlock $h="0.85rem" $w="64%" $mt="0.5rem" />
              <SkeletonBlock $h="3rem" $r="999px" $mt="1.5rem" />
            </>
          ) : wsShowsWall ? (
            /* ── Member without billing management (2026-08-21): the same
                  respectful wall the subscription page shows, plus the growth
                  path that page underplays — start your own organization. ── */
            <>
              <EnterpriseBadge>{ws.label}</EnterpriseBadge>
              <EnterpriseTitle>billing for this workspace</EnterpriseTitle>
              <CardFootNote style={{ textAlign: 'left', marginTop: '0.6rem' }}>
                {ws.kind === 'view' && ws.plan
                  ? <>this organization runs on a <KeepCase>Prest0n</KeepCase> plan · {ws.seats} <KeepCase>Prest0n</KeepCase>{(ws.seats || 1) > 1 ? 's' : ''}. you can view its subscription; changes need the billing permission.</>
                  : <>billing for {ws.label} is managed by its owner.</>}
              </CardFootNote>
              <PrimaryButton
                onClick={(e) => { e.preventDefault(); void sendWsBillingRequest(ws.kind === 'view' && ws.plan ? 'plan_change' : 'hire'); }}
                style={{ width: '100%', marginTop: '1rem' }}
                disabled={wsRequest === 'sending' || wsRequest === 'sent'}
              >
                {wsRequest === 'sent'
                  ? 'request sent to the owner ✓'
                  : ws.kind === 'view' && ws.plan
                    ? 'ask about a plan change'
                    : <>ask the owner to hire <KeepCase>Prest0n</KeepCase></>}
              </PrimaryButton>
              {wsRequest === 'failed' && (
                <CardFootNote>that didn't send — try again in a moment.</CardFootNote>
              )}
              <Divider />
              <CardFootNote style={{ textAlign: 'left' }}>
                need a <KeepCase>Prest0n</KeepCase> of your own? create a new
                organization and hire for it directly.
              </CardFootNote>
              <PrimaryButton
                onClick={(e) => { e.preventDefault(); navigate('/organization'); }}
                style={{ width: '100%', marginTop: '0.75rem' }}
              >
                create a new organization
              </PrimaryButton>
            </>
          ) : (
          <>
          <EnterpriseBadge>
            {isAuthenticated && ws.kind === 'plan'
              ? <>your plan · {ws.label}</>
              : isAuthenticated && ws.kind === 'offer' && ws.scope === 'org'
                ? <>hiring for {ws.label}</>
                : <><KeepCase>Prest0n</KeepCase> professional</>}
          </EnterpriseBadge>
          <EnterpriseTitle>
            {isAuthenticated && ws.kind === 'plan'
              ? 'your full-time digital employee'
              : 'a full-time digital employee'}
          </EnterpriseTitle>
          {/* The one price — recomputed live from the picker below
              (2026-08-16 owner directive: no tier tables, ever). */}
          {(() => {
            const q = hireQuote(hireCadence, hireSeats);
            return (
              <>
                <EnterprisePrice>
                  {fmtUsd(q.perSeatMonthlyUsd)}<PriceUnit>/month</PriceUnit>{' '}
                  <SaveBadge>save {q.savePct}%</SaveBadge>
                </EnterprisePrice>
                <PriceFactList>
                  <PriceFact>
                    per <KeepCase>Prest0n</KeepCase>
                    {hireCadence === 'annual' && <> · a <strong>12-month hire</strong>, billed monthly</>}
                    {hireCadence === 'monthly' && <> · month to month — <strong>cancel anytime</strong></>}
                    {hireCadence === 'prepay' && q.upfrontTotalUsd != null && (
                      <> · <strong>{fmtUsd(q.upfrontTotalUsd)}</strong> once for the year — an extra <strong>{WU_PRICING.prepayDiscountPct}% off</strong></>
                    )}
                  </PriceFact>
                  <PriceFact>
                    includes <strong>{MONTHLY_WU_DISPLAY} PRO Work Units</strong> per <KeepCase>Prest0n</KeepCase>, every month
                  </PriceFact>
                  {hireSeats > 1 && (
                    <PriceFact>
                      {hireSeats} <KeepCase>Prest0n</KeepCase>s · <strong>{fmtUsd(q.totalMonthlyUsd)}/month</strong> ·{' '}
                      {q.totalWuMonthly.toLocaleString('en-US')} pr0
                    </PriceFact>
                  )}
                </PriceFactList>

                <DiscountBlock>
                  <DiscountHead>your hire — price and discount update as you choose</DiscountHead>
                  <CadenceRow role="tablist" aria-label="billing cadence">
                    <CadenceBtn type="button" role="tab" aria-selected={hireCadence === 'monthly'} $active={hireCadence === 'monthly'} onClick={() => setHireCadence('monthly')}>
                      monthly
                    </CadenceBtn>
                    <CadenceBtn type="button" role="tab" aria-selected={hireCadence === 'annual'} $active={hireCadence === 'annual'} onClick={() => setHireCadence('annual')}>
                      annual — billed monthly
                    </CadenceBtn>
                    <CadenceBtn type="button" role="tab" aria-selected={hireCadence === 'prepay'} $active={hireCadence === 'prepay'} onClick={() => setHireCadence('prepay')}>
                      prepay the year — {WU_PRICING.prepayDiscountPct}% off
                    </CadenceBtn>
                  </CadenceRow>
                  <SeatPickRow>
                    <SeatBtnMini
                      type="button"
                      aria-label="fewer Prest0ns"
                      onClick={() => setHireSeats((s) => Math.max(1, s - 1))}
                      disabled={hireSeats <= 1}
                    >
                      −
                    </SeatBtnMini>
                    <SeatNumInput
                      type="number"
                      min={1}
                      value={hireSeats}
                      aria-label="number of Prest0ns"
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isFinite(v)) setHireSeats(v);
                      }}
                      onBlur={() => setHireSeats((s) => Math.max(1, s || 1))}
                    />
                    <SeatBtnMini
                      type="button"
                      aria-label="more Prest0ns"
                      onClick={() => setHireSeats((s) => s + 1)}
                    >
                      +
                    </SeatBtnMini>
                    <SeatPickLabel><KeepCase>Prest0n</KeepCase>{hireSeats > 1 ? 's' : ''} to employ</SeatPickLabel>
                  </SeatPickRow>
                </DiscountBlock>
              </>
            );
          })()}

          {!(isAuthenticated && ws.kind === 'plan') && (
          <>
          <Divider />

          <FeatureList>
            {[
              'works for your whole team',
              'integrates with any system',
              'thousands of built-in tools and skills on day one',
              'fully customizable roles and permissions',
              'self-onboarding — productive immediately',
              'learns your business — persistent memory',
            ].map((feat) => (
              <FeatureItem key={feat}>
                <Check>✓</Check>
                {feat}
              </FeatureItem>
            ))}
          </FeatureList>
          </>
          )}

          {/* State-aware close of the priced card (2026-08-21):
              signed-out → create-account on the STANDARD auth page (signup
              mode → organic grant → unified onboarding); signed-in offer →
              hire for the resolved workspace (checkout lives on the
              subscription page, where the card machinery is); live plan →
              the picker above IS the adjuster, and this button applies it. */}
          {!isAuthenticated ? (
            <>
              <PrimaryButton
                  onClick={(e) => { e.preventDefault(); navigate('/auth', { state: { authMode: 'signup', returnUrl: '/' } }); }}
                  style={{ width: '100%' }}
                >
                  create your account — over 100 jobs on us
                </PrimaryButton>
              <CardFootNote>
                free to start · no credit card
              </CardFootNote>
            </>
          ) : ws.kind === 'plan' ? (
            <>
              <PrimaryButton
                onClick={(e) => { e.preventDefault(); void applyWsChange(); }}
                style={{ width: '100%' }}
                disabled={!ws.canManage || !wsPlanDirty || wsChange.phase === 'applying'}
              >
                {wsChange.phase === 'applying'
                  ? 'applying…'
                  : wsPlanDirty ? 'apply this change' : 'this is your current plan'}
              </PrimaryButton>
              <CardFootNote>
                {wsChange.phase === 'preview' && wsChange.line}
                {wsChange.phase === 'applied' && wsChange.line}
                {wsChange.phase === 'failed' && "that didn't go through — nothing was changed. try again, or use the subscription page."}
                {wsChange.phase === 'idle' && (
                  ws.currentPeriodEnd
                    ? `renews ${new Date(ws.currentPeriodEnd * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                    : 'adjust the term or team size above, then apply'
                )}
                {' · '}
                <InlineLinkBtn type="button" onClick={() => navigate(ws.accountId ? `/subscription?account=${ws.accountId}` : '/subscription')}>
                  full billing &amp; invoices
                </InlineLinkBtn>
              </CardFootNote>
            </>
          ) : (
            <>
              <PrimaryButton
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ws.accountId ? `/subscription?account=${ws.accountId}` : '/subscription');
                }}
                style={{ width: '100%' }}
                disabled={ws.kind === 'loading'}
              >
                {ws.kind === 'loading'
                  ? 'checking your workspace…'
                  : ws.scope === 'org' ? <>hire for {ws.label} →</> : 'hire now →'}
              </PrimaryButton>
              <CardFootNote>
                {ws.isTrial ? 'your trial is running — hiring keeps everything, nothing is lost.' : 'checkout takes about a minute on your subscription page.'}
              </CardFootNote>
            </>
          )}
          </>
          )}
        </EnterpriseCard>
          </EnterpriseSection>
        </RightColumn>
      </TwoColumnLayout>

      {/* (2026-08-16 owner directive: the published-ladder table is gone —
          tiers never render as a table or comparison chart anywhere; the
          hire picker in the card above is the only rate explorer.) */}

      {/* (2026-08-16 owner directive: the "when the work spikes" section is
          gone — its one load-bearing promise moved into the hire card.) */}

      {/* Value proposition band (2026-08-19 owner directive): the two claims
          that separate a Prest0n from a generic AI subscription, with the
          innovation list BEHIND a click — plenty of substance for the buyer
          who wants it, zero wall-of-text for the one who doesn't. */}
      <PricingSection>
        <FeaturesTitle>thousands of built-in tools and skills from day one</FeaturesTitle>
        {/* 2026-08-21 owner revision: the innovations open by default — the
            grid leads, the collapse control trails quietly under it. */}
        {innovationsOpen && (
          <InnovationGrid>
            <FeaturedInnovation>
              a patent-pending memory architecture — it remembers your firm, and gets more accurate the more you use it
            </FeaturedInnovation>
            {INNOVATIONS.map((item) => (
              <InnovationChip key={item}>{item}</InnovationChip>
            ))}
          </InnovationGrid>
        )}
        <InnovationToggleRow>
          <InnovationToggle
            type="button"
            aria-expanded={innovationsOpen}
            onClick={() => setInnovationsOpen((v) => !v)}
          >
            {innovationsOpen ? 'hide the innovations' : 'see the innovations'}{' '}
            <span aria-hidden="true">{innovationsOpen ? '↑' : '↓'}</span>
          </InnovationToggle>
        </InnovationToggleRow>
      </PricingSection>

      {/* What a month buys — ONE section (2026-08-19 owner directive). This
          used to be three: the full rate table, the worked-month sentence, and
          the single-product ceilings sentence — the same arithmetic three
          ways. The calculator carries all of it: preloaded to a real month,
          and each slider's maximum IS that product's ceiling. */}
      <PricingSection>
        <FeaturesTitle>what a month of work looks like — today</FeaturesTitle>
        {/* FACE 2 OF THE 0 (2026-09-09). The five kinds of work in that worked
            month sum to exactly 2,200 pr0 — one Prest0n's allocation — so the
            brand mark and the price are literally the same picture. It is a
            fact, not a layout: zeroMonth.ts throws at import if the rows ever
            stop adding up, rather than drawing a circle that is quietly not a
            month.

            It sits ABOVE the launcher and does not replace it. The ring
            answers "what does a month look like?" at a glance; the modal
            answers "what would MY month cost?" with real sliders. Whether the
            modal is redundant is a decision worth making with this on the page
            rather than before it. */}
        <ZeroMonth />
        <CalculatorRow>
          <WorkCalculatorLauncher variant="bold" cta={isAuthenticated ? 'none' : 'signup'} />
        </CalculatorRow>
      </PricingSection>

      {/* (2026-08-16 owner directive: the "for larger companies" section is
          gone — decision fatigue; the picker in the card already reaches the
          team rates by stepping seats.) */}

      {/* Full capability inventory — compact, names only */}
      <FeaturesSection>
        <FeaturesTitle>everything your ai employee can do</FeaturesTitle>
        <GroupGrid>
          {CAPABILITY_GROUPS.map(({ group, features }) => (
            <GroupCard key={group}>
              <GroupName>{group}</GroupName>
              {features.map((name) => (
                <FeatRow key={name}>
                  <Check>✓</Check>
                  <FeatName>{name}</FeatName>
                </FeatRow>
              ))}
            </GroupCard>
          ))}
        </GroupGrid>

        {/* Train it up */}
        <TrainSection>
          <TrainTitle>A real employee — you train it up</TrainTitle>
          <TrainLead>
            Upload your documents, connect your tools, correct it once — its
            patent-pending memory keeps every lesson, and every night it consolidates the
            day's work so it's measurably smarter each week. Everything it learns
            stays yours and compounds.
          </TrainLead>
          <TrainGrid>
            {TRAINABLE_CAPABILITIES.map((name) => (
              <TrainItem key={name}>
                <Check>✓</Check>
                <span>{name}</span>
              </TrainItem>
            ))}
          </TrainGrid>
        </TrainSection>
      </FeaturesSection>

      {/* Standard site footer + breathing room (2026-08-03 owner report): the
          page used to end at the train-it-up card, so at full scroll the
          floating chat bar sat ON the card's content. The gap plus the
          footer's own tall bottom padding give the bar somewhere to float
          that isn't over anything the user is reading. */}
      {/* The FAQ styled-components below sat DEAD in this file since the
          2026-08-16 directive stripped the comparison table — declared, never
          rendered. The answers were live, but only on /subscription, behind
          ProtectedRoute: the person who needs "can our bill surprise us?"
          answered is the one deciding whether to buy. */}
      <FAQSection id="faq">
        <FAQTitle>{COMMITMENT_FAQ_HEADING}</FAQTitle>
        <FAQList>
          {PRICING_PAGE_FAQ.map((entry) => (
            <FAQItem key={entry.id}>
              <FAQQuestion>{entry.question}</FAQQuestion>
              <FAQAnswer>{entry.answer}</FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
      </FAQSection>

      {/* The five roles, by name and shipped description. The page said
          "fully customizable roles and permissions" in one bullet; this is
          the actual model, and it is the thing law firms buy on. */}
      <FAQSection id="roles">
        <FAQTitle>who can do what</FAQTitle>
        <FAQList>
          {rolesInOrder().map((role) => (
            <FAQItem key={role.key}>
              <FAQQuestion>{role.name}</FAQQuestion>
              {/* The shipped description, for all five. Owner and Admin are
                  `derived` — their grants resolve from the catalog rather than
                  a stored list — but the mirror carries their description too,
                  and it is the same sentence /settings/team shows a customer.
                  This used to be hand-written for those two and named only
                  three of the eight things an Admin never gets. */}
              <FAQAnswer>{role.description}</FAQAnswer>
            </FAQItem>
          ))}
        </FAQList>
        <FAQAnswer style={{ marginTop: '1rem' }}>
          access is resolved for the person asking, on every single message. build your own roles
          from individual permissions, or try the five above on the home page before you sign up.
        </FAQAnswer>
      </FAQSection>

      <TrustBlock />

      <div style={{ height: '3.5rem' }} />
      <GlobalFooter />

      <Modal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        title="book a live demo"
        size="xlarge"
        maxWidth="1100px"
        footer={null}
        noScroll={true}
      >
        <div style={{ height: 'calc(90vh - 80px)', minHeight: '600px', width: '100%', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          <iframe
            src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
            style={{ border: 'none', width: '100%', height: '100%' }}
          ></iframe>
        </div>
      </Modal>
    </Page>
  );
};

export default Pricing;
